import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    QueryList,
    ViewChildren
} from '@angular/core';

import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, skip, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgClass } from '@angular/common';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { mod } from '../../../../helpers/modulo';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import { GetDisabledMsgPipe } from '../../../../pipes/disabled-message.pipe';
import { ShIndeterminateDirective } from '../../../directives/indeterminate.directive';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { isEqual } from 'lodash-es';

@Component({
    selector: 'sh-form-checkbox-list-complex-base',
    templateUrl: './form-checkbox-list-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormCheckboxListComplexBaseComponent)],
    standalone: true,
    imports: [NgClass, ShIndeterminateDirective, AsyncPipe, GetDisabledMsgPipe]
})
export class ShFormCheckboxListComplexBaseComponent
    extends MultiValueFormInput<string>
    implements OnInit, AfterViewChecked
{
    private readonly inputValues$: BehaviorSubject<FormValue<string>[]> = new BehaviorSubject<FormValue<string>[]>([]);
    private readonly state$: BehaviorSubject<FormValue<string>[]> = new BehaviorSubject<FormValue<string>[]>([]);

    public readonly selectOptions$: ReplaySubject<FormSelectOption[]> = new ReplaySubject<FormSelectOption[]>();

    public focusIndex: number = 0;

    @Input() inline = false;
    @Input() id!: string;
    @Input() disabledMsg?: string;

    @ViewChildren('optionElements') optionElements!: QueryList<ElementRef>;

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (this.optionElements) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();

                this.focusIndex = mod(this.focusIndex + 1, this.optionElements.length);
                this.changeDetectorRef.markForCheck();
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();

                this.focusIndex = mod(this.focusIndex - 1, this.optionElements.length);
                this.changeDetectorRef.markForCheck();
                return;
            }
        }

        if (event.key === 'Tab') {
            this.resetFocusIndex();
            this.deactivate();
        }
    }

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
    }

    ngOnInit(): void {
        this.initListeners();
        this.resetFocusIndex();
    }

    ngAfterViewChecked() {
        if (this.isActiveComponent()) {
            const items = this.optionElements.toArray();
            if (items[this.focusIndex]) {
                items[this.focusIndex].nativeElement.focus();
            }
        }
    }

    @Input() set options(options: FormSelectOption[]) {
        if (options) {
            this.selectOptions$.next(options);
        }
    }

    writeValueImpl(value: FormValue<string>[]): void {
        this.inputValues$.next(value);
    }

    private initListeners(): void {
        combineLatest([this.selectOptions$, this.inputValues$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([options, formValues]) => {
                this.state$.next(
                    options.map(formSelectOption => {
                        const value: string = formSelectOption.value;
                        const formValue: FormValue<string> | undefined = formValues.find(fv => fv.value === value);
                        const state: FormValueState = formValue ? formValue.state : FormValueState.UNSET;
                        return new FormValue(formSelectOption.value, state);
                    })
                );
            });

        // Don't emit initial state
        this.state$.pipe(distinctUntilChanged(isEqual), skip(1), takeUntil(this.destroy$)).subscribe(values => {
            this.propagateChange(values);
        });
    }

    toggleCheckbox(event: Event, selectedOption: FormSelectOption): void {
        const state: FormValueState = (event.target as HTMLInputElement).checked
            ? FormValueState.SET
            : FormValueState.UNSET;

        const currentValue: FormValue<string>[] = [...this.state$.value];
        const index: number = currentValue.findIndex(fv => fv.value === selectedOption.value);

        if (index > -1) {
            const formValue: FormValue<string> = new FormValue(currentValue[index].value, state);
            currentValue.splice(index, 1, formValue);
        } else {
            console.error('ShFormCheckboxListComplexBaseComponent:: FormValue should always be in the list');
        }

        this.state$.next(currentValue);
    }

    isIndeterminate(formSelectOption: FormSelectOption): boolean {
        const formValue: FormValue<string> | undefined = this.state$.value.find(
            fv => fv.value === formSelectOption.value
        );
        if (typeof formValue === 'undefined') {
            return false;
        }
        return formValue.state === FormValueState.INDETERMINATE;
    }

    isSet(formSelectOption: FormSelectOption): boolean {
        const formValue: FormValue<string> | undefined = this.state$.value.find(
            fv => fv.value === formSelectOption.value
        );
        if (typeof formValue === 'undefined') {
            return false;
        }
        return formValue.state === FormValueState.SET;
    }

    private resetFocusIndex(): void {
        this.selectOptions$.pipe(take(1)).subscribe(options => {
            this.focusIndex = options.findIndex(option => !option.disabled); // Find the first active option
        });
    }

    public onFocus(): void {
        this.activate();
    }
}
